/* eslint-disable max-len */
import React, { useCallback, useMemo, useState } from 'react'
import {
  ActionButtons,
  DataTable,
  type DataTableColumn,
  type DataTableExpandedTableConfig,
  type DataTableRow,
} from '@enamelai/design-system'
import { Icon as CallIcon } from '@enamelai/design-system/icons/Call'
import { Icon as MailIcon } from '@enamelai/design-system/icons/Mail_line'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { useFormsTableState } from '@components/pages/FormBuilder/Shared/useFormsTableState'
import AssignFormsModal from './AssignFormsModal.component'
import PatientOTPModal from '@components/pages/FormBuilder/PendingFormsTable/PatientOTPModal.component'
import type { PendingFormEntry } from '@pages/FormBuilder/assignedForms.types'
import './SendingFormsTable.css'
import { setActiveSidePanel } from '@store/reducers/uiSlice.reducer'
import { SET_INPUT_NUMBER } from '@store/reducers/messageCenter.reducer'
import { normalizePhoneNumber } from '@utils/numberFormatter.util'
import type { IContact } from '@dataTypes/contact.type'
import SelectContactModal from '@components/layout/Modals/SelectContact/SelectContactModal.component'
import { createPatientFormsOtp } from '@services/api/otp.api'
import { useNotificationToast } from '@context/NotificationToastContext'
import { createFailedToast } from '@utils/notificationToast.util'

export interface SendingAppointmentDetail {
  appointmentId: string
  appointmentType: string
  roomNo: string
  appointmentDate: string
  lastReminded: string
}

export interface SendingFormEntry {
  patientId: string
  publicPatientId: string
  patientName: string
  patientMobile?: string
  numberOfAppointments: number
  appointments: SendingAppointmentDetail[]
}

type SendingFormRow = {
  id: string
  patientName: string
  numberOfAppointments: number
}

type AppointmentRow = {
  id: string
  request: string
  appointmentDate: string
}

const getRequestLabel = (appointment: SendingAppointmentDetail): string =>
  `${appointment.appointmentType} - ${appointment.roomNo}`

interface SendingFormsTableProps {
  data: SendingFormEntry[]
  onFormsAssigned?: (entry: PendingFormEntry) => void
}

const SendingFormsTable: React.FC<SendingFormsTableProps> = ({
  data,
  onFormsAssigned,
}) => {
  const dispatch = useAppDispatch()
  const { showToast } = useNotificationToast()
  const contacts = useAppSelector((state) => state.contacts.data)
  const [expandedRowIds, setExpandedRowIds] = useState<string[]>([])
  const [showAssignModal, setShowAssignModal] = useState(false)
  const [assignModalRow, setAssignModalRow] = useState<SendingFormEntry | null>(
    null,
  )
  const [showOTPModal, setShowOTPModal] = useState(false)
  const [showSelectContactModal, setShowSelectContactModal] = useState(false)
  const [selectedContact, setSelectedContact] = useState<IContact | null>(null)
  const [otpModalData, setOtpModalData] = useState<{
    patientName: string
    otp: string
    otpId?: string
    appointmentId?: string
    patientId: string
  } | null>(null)

  const rows = useMemo(
    (): SendingFormRow[] =>
      data.map((entry) => ({
        id: entry.patientId,
        patientName: entry.patientName,
        numberOfAppointments: entry.numberOfAppointments,
      })),
    [data],
  )
  const entryById = useMemo(
    () => new Map(data.map((entry) => [entry.patientId, entry])),
    [data],
  )
  const getSortValue = useCallback(
    (row: SendingFormRow, columnId: string): string | number =>
      columnId === 'numberOfAppointments'
        ? row.numberOfAppointments
        : row.patientName,
    [],
  )
  const table = useFormsTableState(rows, getSortValue)

  const columns: DataTableColumn<SendingFormRow>[] = [
    { id: 'patientName', label: 'Patient', sortable: true },
    {
      id: 'numberOfAppointments',
      label: 'No. of appointments',
      sortable: true,
      width: 200,
    },
  ]

  const callPatient = (row: SendingFormEntry): void => {
    const patientContact = contacts.find(
      (contact) => contact.patient_id === row.patientId,
    )
    if (patientContact) {
      setSelectedContact(patientContact)
      setShowSelectContactModal(true)
      return
    }
    const dialNumber = row.patientMobile
      ? normalizePhoneNumber(row.patientMobile)
      : ''
    if (dialNumber) {
      dispatch(SET_INPUT_NUMBER(dialNumber))
    }
    dispatch(setActiveSidePanel('phone'))
  }

  const renderActions = (row: SendingFormRow): React.ReactElement | null => {
    const entry = entryById.get(row.id)
    if (!entry) return null
    return (
      <ActionButtons
        actions={[
          {
            id: 'call',
            icon: <CallIcon className="ds-action-button__svg" aria-hidden />,
            label: 'Phone',
            tone: 'primary',
            variant: 'solid',
            onClick: () => callPatient(entry),
          },
          {
            id: 'assign-forms',
            icon: <MailIcon className="ds-action-button__svg" aria-hidden />,
            label: 'Create/send forms',
            tone: 'primary',
            variant: 'outline',
            onClick: () => {
              setAssignModalRow(entry)
              setShowAssignModal(true)
            },
          },
        ]}
      />
    )
  }

  const expandedTable: DataTableExpandedTableConfig<
    DataTableRow,
    SendingFormRow
  > = {
    columns: [
      { id: 'request', label: 'Appointment - Room no.' },
      { id: 'appointmentDate', label: 'Date', width: 200 },
    ],
    getRows: (parentRow): AppointmentRow[] =>
      (entryById.get(parentRow.id)?.appointments ?? []).map((appointment) => ({
        id: `${parentRow.id}:${appointment.appointmentId}`,
        request: getRequestLabel(appointment),
        appointmentDate: appointment.appointmentDate || '—',
      })),
  }

  if (data.length === 0) {
    return null
  }

  return (
    <div className="sending-forms-table-wrapper">
      <DataTable<SendingFormRow>
        className="sending-forms-table"
        columns={columns}
        rows={table.pageRows}
        expandable
        expandableColumnId="patientName"
        expandedRowIds={expandedRowIds}
        onExpandedRowIdsChange={setExpandedRowIds}
        expandedTable={expandedTable}
        sortColumnId={table.sortColumnId}
        sortDirection={table.sortDirection}
        onSort={table.onSort}
        pagination={table.pagination}
        showActions
        actionsLabel="Action"
        renderActions={renderActions}
      />
      {showAssignModal && assignModalRow && (
        <AssignFormsModal
          show={showAssignModal}
          onHide={() => {
            setShowAssignModal(false)
            setAssignModalRow(null)
          }}
          patientName={assignModalRow.patientName}
          patientId={assignModalRow.patientId}
          publicPatientId={assignModalRow.publicPatientId}
          patientMobile={assignModalRow.patientMobile}
          appointments={assignModalRow.appointments}
          onOTPShow={(
            patientName: string,
            otp: string,
            otpId?: string,
            appointmentId?: string,
          ) => {
            setOtpModalData({
              patientName,
              otp,
              otpId,
              appointmentId,
              patientId: assignModalRow.patientId,
            })
            setShowOTPModal(true)
          }}
          onFormsAssigned={onFormsAssigned}
        />
      )}
      {showOTPModal && otpModalData && (
        <PatientOTPModal
          show={showOTPModal}
          onHide={() => {
            setShowOTPModal(false)
            setOtpModalData(null)
          }}
          patientName={otpModalData.patientName}
          otp={otpModalData.otp}
          patientId={otpModalData.patientId}
          onRegenerate={() => {
            void createPatientFormsOtp({
              patient_id: otpModalData.patientId,
              recipient: 'kiosk',
              ...(otpModalData.appointmentId && {
                appointment_id: otpModalData.appointmentId,
              }),
            })
              .then((result) => {
                if (!result.code) throw new Error('Missing invitation code')
                setOtpModalData({
                  ...otpModalData,
                  otp: result.code,
                  otpId: result.otp_id,
                })
              })
              .catch(() =>
                showToast(
                  createFailedToast(
                    'Error',
                    'Failed to regenerate OTP. Please try again.',
                  ),
                ),
              )
          }}
        />
      )}
      {showSelectContactModal && selectedContact && (
        <SelectContactModal
          show={showSelectContactModal}
          onHide={() => {
            setShowSelectContactModal(false)
            setSelectedContact(null)
          }}
          contactData={selectedContact}
        />
      )}
    </div>
  )
}

export default SendingFormsTable
